import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import OnboardingModalShell from '../components/OnboardingModalShell';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  fetchPublicMembershipConfig,
  createPremiumAddonRazorpayOrder,
  fetchPremiumAddonQuote,
  verifyPremiumAddonRazorpay,
  checkoutPremiumAddon,
  completeOnboarding,
  normalizeStudioApiError,
} from '../services/studioOnboardingApi';
import { loadRazorpayScript } from '../utils/loadRazorpay';
import { membershipAllowsPremiumAddonQuote } from '../utils/premiumAddonEligibility';
import KidBackground from '../ui/backgrounds/KidBackground';
import RoomSceneWrapper from '../components/artHistoryRoom/RoomSceneWrapper';

/** Payment / order calls use ~30s internal caps — outer limit must exceed them. */
const API_ACTION_TIMEOUT_MS = 45000;

function withTimeout(promise, ms, message) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(message)), ms)),
  ]);
}

/** Readable comparison rows — Base user vs Premium user. */
const PLAN_COMPARISON = [
  {
    title: 'Core Studio & learning',
    base: 'Full access with monthly limits on lessons and tools.',
    premium:
      'Unlimited headroom: use core Studio tools without hitting Base caps.',
  },
  {
    title: 'Art History & Compare',
    base: 'Included with monthly limits.',
    premium: 'Unlimited exploration — go deep without tight caps.',
  },
  {
    title: 'Style transfer & daily activities',
    base: 'Fair-use limits each month.',
    premium: 'Much higher limits — unlimited-style use through your access period.',
  },
  {
    title: 'Co-learning & Reimagine Studio',
    base: 'Included within Base limits.',
    premium: 'Unlimited Co-learning & Reimagine for your access period.',
  },
  {
    title: 'Storyboarding (“Once Upon a Click”)',
    base: 'Not included for Base users.',
    premium: 'Unlimited Storyboarding — full “Once Upon a Click” experience.',
  },
];

export default function ArtmoRedeemSuccessPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, refreshMembership, membership: membershipStatus } = useAuth();
  const stateMonths = location.state?.monthsGranted;
  const statePeriodEnd = location.state?.periodEnd;

  const statusPayload = membershipStatus && typeof membershipStatus === 'object' ? membershipStatus : {};
  const mem = statusPayload.membership || {};

  const [config, setConfig] = useState({
    premiumAddonMonthlyPrice: 200,
    currency: 'INR',
    isUpgradeEnabled: true,
    razorpayConfigured: false,
    /** Until /membership-public/membership-config loads, do not assume dev simulate (avoids 400 use_razorpay). */
    premiumSimulateAllowed: false,
  });
  const [premiumQuote, setPremiumQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(true);
  /** False until /membership-public/membership-config resolves (simulate vs Razorpay). */
  const [membershipConfigReady, setMembershipConfigReady] = useState(false);
  const [loading, setLoading] = useState(false);
  /** Which primary action is running — for button labels when both are visible. */
  const [pendingAction, setPendingAction] = useState(null);
  const [error, setError] = useState('');
  /** choose: comparison + actions; success_*: confirmation before welcome modal. */
  const [flowStep, setFlowStep] = useState('choose');
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [includeStoryboardingAddon, setIncludeStoryboardingAddon] = useState(false);
  const [showManualCheckout, setShowManualCheckout] = useState(false);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualError, setManualError] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardName, setCardName] = useState('');
  const [cardExpiry, setCardExpiry] = useState(''); // MM/YY
  const [cardCvc, setCardCvc] = useState('');
  /** Base path: brief full-screen loader while server registers access, then success modal. */
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [simCheckoutOpen, setSimCheckoutOpen] = useState(false);
  const [simCardEmail, setSimCardEmail] = useState('');
  const [simCardName, setSimCardName] = useState('');
  const [simCardNumber, setSimCardNumber] = useState('');
  const [simCardExp, setSimCardExp] = useState('');
  const [simCardCvc, setSimCardCvc] = useState('');
  const [simFormError, setSimFormError] = useState('');

  // Billing/invoice fields for the simulated checkout UI.
  const [billingName, setBillingName] = useState('');
  const [billingEmail, setBillingEmail] = useState('');
  const [billingAddress1, setBillingAddress1] = useState('');
  const [billingAddress2, setBillingAddress2] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingPostalCode, setBillingPostalCode] = useState('');
  const [billingPhone, setBillingPhone] = useState('');

  // Payment method toggle for the simulated checkout UI.
  const [paymentMethod, setPaymentMethod] = useState('card'); // 'card' | 'upi'
  const [simUpiId, setSimUpiId] = useState('');

  const formatExpiryInput = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 4);
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  };

  const isValidSimCard = () => {
    const digits = simCardNumber.replace(/\D/g, '');
    const expOk = /^(0[1-9]|1[0-2])\/(\d{2})$/.test(String(simCardExp || '').trim());
    return (
      simCardEmail.trim().length > 3 &&
      simCardName.trim().length > 1 &&
      digits.length >= 12 &&
      expOk &&
      String(simCardCvc || '').trim().length >= 3
    );
  };

  const isValidSimUpi = () => {
    const v = String(simUpiId || '').trim().toLowerCase();
    // Basic UPI ID check like `name@bank` (we keep it permissive for UI simulation).
    return (
      simCardEmail.trim().length > 3 &&
      v.length >= 5 &&
      /^[a-z0-9.\-_]{2,}@[a-z]{2,}$/.test(v)
    );
  };

  const monthsFromProfile =
    mem.artmoCodeMonthsGranted ??
    statusPayload.artmoCodeMonthsGranted ??
    mem.artmoPendingMonths ??
    mem.artmo?.months;
  const months = typeof stateMonths === 'number' && stateMonths > 0 ? stateMonths : Number(monthsFromProfile) || 1;

  // Source of truth: prefer the actual nested flag `membership.premiumAddon.applied`.
  // `statusPayload.hasPremiumAddon` can lag / be stale in edge downgrade cases, which would incorrectly skip checkout.
  const hasPremiumAddon =
    typeof mem.premiumAddon?.applied === 'boolean'
      ? mem.premiumAddon.applied
      : !!statusPayload.hasPremiumAddon;
  const featureLimits = statusPayload.featureLimits || {};

  const formatPeriodLimit = (limit) => {
    if (limit === 'unlimited') return 'Unlimited';
    const s = typeof limit === 'string' ? limit.trim().toLowerCase() : '';
    if (s === 'unlimited') return 'Unlimited';
    const n = typeof limit === 'number' ? limit : parseInt(String(limit || ''), 10);
    if (!Number.isNaN(n)) return `${n} / month`;
    if (limit == null) return '—';
    return String(limit);
  };

  const artHistoryAppreciationLimit = formatPeriodLimit(featureLimits.artHistoryAppreciation?.limit);
  const artComparisonLimit = formatPeriodLimit(featureLimits.artComparison?.limit);
  const styleTransferLimit = formatPeriodLimit(featureLimits.styleTransfer?.limit);
  const dailyActivitiesLimit = formatPeriodLimit(featureLimits.dailyActivities?.limit);
  const artAppreciationLimit = formatPeriodLimit(featureLimits.individualArtAppreciation?.limit);
  const coLearningLimit = formatPeriodLimit(featureLimits.coLearningSpace?.limit);
  const reimagineLimit = formatPeriodLimit(featureLimits.reimagineStudio?.limit);
  const storyboardingEnabled = featureLimits.storyboarding?.enabled === true;
  const storyboardingLimitText = formatPeriodLimit(featureLimits.storyboarding?.limit);

  const styleTransferLimitRaw = featureLimits.styleTransfer?.limit;
  const styleTransferNumericLimit =
    styleTransferLimitRaw === 'unlimited'
      ? null
      : typeof styleTransferLimitRaw === 'number'
        ? styleTransferLimitRaw
        : parseInt(String(styleTransferLimitRaw ?? ''), 10);
  const styleTransferArtformsText =
    styleTransferNumericLimit != null && !Number.isNaN(styleTransferNumericLimit)
      ? `Up to ${styleTransferNumericLimit * 5}+ artforms (3 styles + tutorial set per artwork)`
      : 'Up to 3 styles + tutorial sets per artwork (unlimited when enabled)';

  useEffect(() => {
    if (user?.uid) {
      refreshMembership(user.uid, { force: true }).catch(() => {});
    }
  }, [user?.uid, refreshMembership]);

  useEffect(() => {
    fetchPublicMembershipConfig()
      .then((c) => {
        if (c && c.ok !== false) {
          setConfig({
            premiumAddonMonthlyPrice: c.premiumAddonMonthlyPrice ?? 200,
            currency: c.currency || 'INR',
            isUpgradeEnabled: c.isUpgradeEnabled !== false,
            razorpayConfigured: !!c.razorpayConfigured,
            premiumSimulateAllowed: !!c.premiumSimulateAllowed,
          });
        }
      })
      .catch(() => {
        setConfig((prev) => ({
          ...prev,
          premiumSimulateAllowed: false,
          razorpayConfigured: false,
        }));
      })
      .finally(() => setMembershipConfigReady(true));
  }, []);

  /** Load Razorpay early so Upgrade doesn’t wait on the script tag (avoids long “Processing…”). */
  useEffect(() => {
    if (flowStep !== 'choose' || !user?.uid || hasPremiumAddon || !config.isUpgradeEnabled) return;
    void loadRazorpayScript().catch(() => {});
  }, [flowStep, user?.uid, hasPremiumAddon, config.isUpgradeEnabled]);

  useEffect(() => {
    if (!user || hasPremiumAddon || !config.isUpgradeEnabled) {
      setQuoteLoading(false);
      return;
    }
    const payload =
      membershipStatus && typeof membershipStatus === 'object' ? membershipStatus : null;
    if (!membershipAllowsPremiumAddonQuote(payload)) {
      setPremiumQuote(null);
      setQuoteLoading(false);
      return;
    }
    let cancelled = false;
    const slowQuoteTimer = setTimeout(() => {
      if (!cancelled) setQuoteLoading(false);
    }, 12000);
    fetchPremiumAddonQuote()
      .then((q) => {
        if (!cancelled && q?.ok !== false) setPremiumQuote(q);
      })
      .catch(() => {
        if (!cancelled) setPremiumQuote(null);
      })
      .finally(() => {
        clearTimeout(slowQuoteTimer);
        if (!cancelled) setQuoteLoading(false);
      });
    return () => {
      cancelled = true;
      clearTimeout(slowQuoteTimer);
    };
  }, [user, hasPremiumAddon, config.isUpgradeEnabled, membershipStatus]);

  const currencyLabel = config.currency === 'INR' ? '₹' : `${config.currency} `;
  const canPaySimulated =
    paymentMethod === 'card' ? isValidSimCard() : paymentMethod === 'upi' ? isValidSimUpi() : false;

  /**
   * After Base / unlimited onboarding actions: Artist Corner if they already have a profile doc,
   * otherwise profile creation (first-time users).
   */
  const getPostOnboardingDestination = useCallback(async () => {
    if (!user?.uid) return '/artist-profile';
    try {
      const snap = await Promise.race([
        getDoc(doc(db, 'artistProfiles', user.uid)),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('artist_profile_read_timeout')), 12000)
        ),
      ]);
      if (snap.exists()) {
        return '/my-art-world';
      }
    } catch (_) {
      // Network / timeout: safer to send first-timers to profile creation
    }
    return '/artist-profile';
  }, [user?.uid]);

  const goToArtistCorner = useCallback(async () => {
    setWelcomeOpen(false);
    const path = await getPostOnboardingDestination();
    navigate(path, { replace: true, state: { paymentSuccess: true } });
  }, [getPostOnboardingDestination, navigate]);

  /** Never await in the success path — /membership/status can be slow; AuthContext updates in the background. */
  const kickRefreshMembership = useCallback(() => {
    if (!user?.uid) return;
    void refreshMembership(user.uid, { force: true }).catch((err) => {
      console.warn('refreshMembership (background):', err?.message || err);
    });
  }, [user?.uid, refreshMembership]);

  const continueWithLimited = () => {
    if (!user?.uid) return;
    setError('');
    setFlowStep('granting_access');
    setPendingAction('continue');
    kickRefreshMembership();
    setLoading(false);
    setWelcomeOpen(false);

    void (async () => {
      try {
        await completeOnboarding('artmo_limited');
        if (!mountedRef.current) return;
        await goToArtistCorner();
      } catch (e) {
        if (!mountedRef.current) return;
        setError(normalizeStudioApiError(e));
        setFlowStep('choose');
      } finally {
        if (!mountedRef.current) return;
        setPendingAction(null);
        setLoading(false);
      }
    })();
  };

  const runPremiumUpgrade = async () => {
    if (!user?.uid) {
      setError('You need to be signed in to upgrade.');
      return;
    }
    if (!config.isUpgradeEnabled) {
      setError('Upgrades are temporarily unavailable. Try again later or use Manage subscription.');
      return;
    }
    setPendingAction('upgrade');
    setError('');

    const openRazorpayCheckout = async () => {
      if (typeof window.Razorpay !== 'function') {
        throw new Error('Payment could not be loaded. Please refresh and try again.');
      }
      const order = await withTimeout(
        createPremiumAddonRazorpayOrder(),
        API_ACTION_TIMEOUT_MS,
        'Could not start payment in time. Check your connection and try again.'
      );
      if (!order?.keyId) {
        throw new Error('Payment could not start (missing gateway key). If this keeps happening, contact support.');
      }

      const isFixedOnboarding = order.pricingMode === 'fixed_artmo_onboarding';
      const descParts = [];
      if (isFixedOnboarding && order.fixedMonths != null) {
        descParts.push(`Fixed ${order.fixedMonths} month(s) × ₹${order.monthlyRate ?? config.premiumAddonMonthlyPrice}/mo`);
      } else if (order.billableMonths != null) {
        const d = order.prorationMonthDays ?? 30;
        descParts.push(`Prorated ~${Number(order.billableMonths).toFixed(2)} × ${d}-day month(s)`);
      }
      if (order.periodEnd) {
        try {
          descParts.push(`through ${new Date(order.periodEnd).toLocaleDateString()}`);
        } catch (_) {
          descParts.push('for your current access period');
        }
      }

      const description =
        descParts.length > 0
          ? `Unlimited add-on + Storyboarding (${descParts.join(', ')})`
          : `Studio unlimited add-on + Storyboarding (${isFixedOnboarding ? 'fixed months' : 'prorated'})`;

      const opts = {
        key: order.keyId,
        amount: order.amount,
        currency: order.currency || 'INR',
        order_id: order.orderId,
        name: 'Artmosphere Studio',
        description,
        handler: async (response) => {
          setPendingAction('upgrade');
          setLoading(true);
          setError('');
          try {
            await verifyPremiumAddonRazorpay({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setFlowStep('success_premium');
            kickRefreshMembership();
            void goToArtistCorner();
          } catch (err) {
            setError(normalizeStudioApiError(err));
          } finally {
            if (!mountedRef.current) return;
            setLoading(false);
            setPendingAction(null);
          }
        },
        prefill: {
          email: user?.email || '',
          name: user?.displayName || '',
        },
        theme: { color: '#d97706' },
        modal: {
          ondismiss: () => {
            setLoading(false);
            setPendingAction(null);
          },
        },
      };

      const rzp = new window.Razorpay(opts);
      rzp.on('payment.failed', (resp) => {
        setError(resp?.error?.description || 'Payment failed.');
        setLoading(false);
        setPendingAction(null);
      });
      setLoading(false);
      setPendingAction(null);
      rzp.open();
    };

    // If Razorpay is configured, open the real checkout.
    if (config.razorpayConfigured) {
      try {
        await withTimeout(
          loadRazorpayScript(),
          15000,
          'Payment script timed out. Check your network or try again.'
        );
        if (typeof window.Razorpay !== 'function') {
          throw new Error('Payment could not be loaded. Please refresh and try again.');
        }

        setLoading(true);
        await openRazorpayCheckout();
      } catch (e) {
        const msg = normalizeStudioApiError(e);
        if (config.premiumSimulateAllowed && String(msg || '').includes('razorpay_not_configured')) {
          setSimFormError('');
          setSimCheckoutOpen(true);
          setLoading(false);
          setPendingAction(null);
          return;
        }
        setError(msg);
        setLoading(false);
        setPendingAction(null);
      }
      return;
    }

    // Otherwise, open our own checkout modal with card inputs.
    // Even if simulation is disabled, we still show the modal so the user sees the checkout step.
    setSimFormError('');
    setSimCheckoutOpen(true);
    setLoading(false);
    setPendingAction(null);
    if (!config.premiumSimulateAllowed) {
      setSimFormError('Payments are temporarily unavailable. Please try again later or use Manage subscription.');
    }
  };

  const handleContinueOnboarding = () => {
    // If premium add-on is already applied, we only need to finish base onboarding and move to books.
    if (hasPremiumAddon || !includeStoryboardingAddon) {
      continueWithLimited();
      return;
    }

    if (!config.isUpgradeEnabled) {
      setError('Upgrades are temporarily unavailable. Please try again later.');
      return;
    }

    runPremiumUpgrade();
  };

  const premiumCheckoutMonths = (() => {
    const fixed = premiumQuote?.fixedMonths;
    const billable = premiumQuote?.billableMonths;
    const n = fixed != null ? Number(fixed) : billable != null ? Number(billable) : null;
    if (n == null || Number.isNaN(n)) return Math.max(1, months);
    return n;
  })();

  const premiumCheckoutAmountRupees =
    premiumQuote?.amountRupees ??
    (config.premiumAddonMonthlyPrice * premiumCheckoutMonths);

  const premiumAddonSubtotal = Number(premiumCheckoutAmountRupees || 0);
  const PREMIUM_GST_RATE = 0.18;
  const premiumAddonGst = premiumAddonSubtotal * PREMIUM_GST_RATE;
  const premiumAddonTotal = premiumAddonSubtotal + premiumAddonGst;

  const completeSimulatedPremiumCheckout = async () => {
    if (!user?.uid) {
      setSimFormError('You need to be signed in to upgrade.');
      return;
    }
    if (paymentMethod === 'card') {
      if (!isValidSimCard()) {
        setSimFormError('Please fill all card payment details correctly.');
        return;
      }
    } else if (!isValidSimUpi()) {
      setSimFormError('Please enter a valid UPI ID (example: name@bank).');
      return;
    }
    if (!config.premiumSimulateAllowed) {
      setSimFormError('Payments are temporarily unavailable. Please try again later or use Manage subscription.');
      return;
    }

    setSimFormError('');
    setSimCheckoutOpen(false);
    setLoading(true);
    setPendingAction('upgrade');
    setError('');

    try {
      await checkoutPremiumAddon();
      setFlowStep('success_premium');
      kickRefreshMembership();
      void goToArtistCorner();
    } catch (e) {
      setError(normalizeStudioApiError(e));
      setFlowStep('choose');
    } finally {
      if (!mountedRef.current) return;
      setLoading(false);
      setPendingAction(null);
    }
  };

  const periodEndLabel = (() => {
    if (statePeriodEnd) {
      try {
        return new Date(statePeriodEnd).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch (_) {
        return null;
      }
    }
    const raw = mem.currentPeriodEnd || mem.expiresAt;
    if (!raw) return null;
    const d = typeof raw.toDate === 'function' ? raw.toDate() : new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  })();

  const premiumAmountFormatted =
    premiumQuote?.amountRupees != null
      ? `${currencyLabel}${Number(premiumQuote.amountRupees).toLocaleString('en-IN')}`
      : null;
  const showSuccessModal = flowStep === 'success_base' || flowStep === 'success_premium';

  return (
    <>
      <KidBackground variant="blobs" intensity={0.45} />
      <RoomSceneWrapper>
        <div className="w-full px-[max(0.75rem,1.5cm)] py-4 sm:py-6 box-border">
        <div className="w-full rounded-2xl border border-amber-200/60 bg-white/95 shadow-[0_16px_48px_-20px_rgba(180,83,9,0.30)] ring-1 ring-amber-900/5 overflow-hidden">
          <div className="px-4 sm:px-8 py-4 sm:py-5 text-center bg-gradient-to-r from-amber-400 via-orange-400 to-orange-500">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white leading-snug drop-shadow-sm">
              <>
                You&apos;re all set — <span className="text-amber-100">{months} month{months === 1 ? '' : 's'}</span> as an{' '}
                <span className="text-white">Artmosphere Studio access</span>
                {periodEndLabel ? (
                  <span className="block sm:inline text-white/95 font-normal text-base mt-1 sm:mt-0 sm:ml-1">
                    · access through <span className="font-semibold text-white">{periodEndLabel}</span>
                  </span>
                ) : null}
              </>
            </h1>
            <p className="mt-2 text-xs sm:text-sm text-white/90 max-w-2xl mx-auto leading-relaxed">
              Your Artmo code is active. Add the Unlimited add-on below to unlock <strong>no limits</strong> +{' '}
              <strong>Storyboarding</strong> for your current months.
            </p>
          </div>

          <div className="px-4 sm:px-8 md:px-10 py-5 sm:py-6 space-y-5">
            <>
            <div className="flex flex-wrap gap-3 text-sm text-stone-800 bg-amber-50/60 border border-amber-200/70 rounded-xl px-4 py-3">
              <span>
                <span className="text-stone-600">Artmo code access:</span>{' '}
                <strong>{months} month{months === 1 ? '' : 's'}</strong>
              </span>
              {periodEndLabel ? (
                <span className="text-stone-700">
                  <span className="text-stone-600">Access until:</span> <strong>{periodEndLabel}</strong>
                </span>
              ) : null}
            </div>

            <div className="space-y-4">
              <div className="rounded-xl border border-amber-200/70 bg-white/70 px-4 py-3 text-sm text-stone-800">
                <div className="font-semibold text-amber-950">
                  Artmosphere Studio is active for {months} month{months === 1 ? '' : 's'}.
                </div>
                <div className="text-xs text-stone-600 mt-1 leading-relaxed">
                  Your Artmo code unlocks your Studio access for the same period.
                </div>
              </div>

              <div className="rounded-2xl border border-amber-200/70 bg-white/70 px-4 py-4 sm:px-5 sm:py-5">
                <div className="font-semibold text-amber-950 mb-2">Artmosphere Studio access with limited features</div>
                <ul className="space-y-2 text-sm text-stone-700">
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">✓</span>
                    <span className="font-medium">Art History Learning</span>: {artHistoryAppreciationLimit} • Learn art history, techniques, and story-based insights
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">✓</span>
                    <span className="font-medium">Ways of Seeing</span>: {artComparisonLimit} • Compare artworks to understand differences
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">✓</span>
                    <span className="font-medium">Style Transfer</span>: {styleTransferLimit} • {styleTransferArtformsText}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">✓</span>
                    <span className="font-medium">Daily Activity AI feedback</span>: {dailyActivitiesLimit} • Daily practice with quick AI guidance
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">✓</span>
                    <span className="font-medium">Art Appreciation Tutorials</span>: {artAppreciationLimit} • Self-learning tutorials to sharpen your skills
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">✓</span>
                    <span className="font-medium">Co-learning Space</span>: {coLearningLimit} • Collaborate and learn through shared activities
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-emerald-600 mt-0.5">✓</span>
                    <span className="font-medium">Reimagine Studio</span>: {reimagineLimit} • Create reimagined variations with guidance
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-500 mt-0.5">✕</span>
                    <span className="font-medium">Storyboarding (“Once Upon a Click”)</span>: 0 / month (locked) • Create characters and storybooks
                  </li>
                </ul>
              </div>

              <div className="rounded-2xl border border-amber-200/70 bg-amber-50/30 px-4 py-4 sm:px-5 sm:py-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-400"
                    checked={hasPremiumAddon ? true : includeStoryboardingAddon}
                    disabled={hasPremiumAddon || loading || !config.isUpgradeEnabled || !membershipConfigReady}
                    onChange={(e) => setIncludeStoryboardingAddon(e.target.checked)}
                    aria-label="Add unlimited Storyboarding add-on"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-base font-bold text-amber-950">
                          Add-on @ {currencyLabel}
                          {config.premiumAddonMonthlyPrice}/month
                        </div>
                        <div className="text-xs text-stone-600 mt-0.5">Unlimited + Storyboarding for your Artmo months.</div>
                      </div>
                      {hasPremiumAddon ? (
                        <span className="rounded-full bg-amber-600 px-2 py-0.5 text-[11px] font-bold uppercase text-white">
                          Active
                        </span>
                      ) : null}
                    </div>

                    <p className="mt-2 text-sm text-stone-700 leading-relaxed">
                      Everything stated in your Artmosphere Studio access, now with <strong>no limits</strong>, plus
                      <strong> Storyboarding</strong> to create your own characters, write stories, and print your storybooks
                      with Artmosphere.
                    </p>
                  </div>
                </label>
              </div>

              {error ? (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  {error}
                </div>
              ) : null}

              <div className="pt-1 border-t border-stone-200/70">
                <button
                  type="button"
                  disabled={
                    loading ||
                    simCheckoutOpen ||
                    (includeStoryboardingAddon && !hasPremiumAddon && (!config.isUpgradeEnabled || !membershipConfigReady))
                  }
                  onClick={handleContinueOnboarding}
                  className="art-btn-primary min-h-[52px] w-full rounded-full text-sm font-semibold disabled:opacity-50 px-3 flex items-center justify-center"
                >
                  {loading && pendingAction ? 'Processing…' : 'Continue onboarding'}
                </button>
              </div>

              <p className="text-center text-xs text-stone-600 max-w-2xl mx-auto leading-relaxed">
                {hasPremiumAddon
                  ? 'Unlimited add-on is already active. You can start creating your artist profile.'
                  : includeStoryboardingAddon
                    ? 'Checkout will open to activate Unlimited + Storyboarding.'
                    : 'No checkout needed. You’ll see the Artmosphere Studio welcome and start creating your artist profile.'}
              </p>
            </div>
            </>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {flowStep === 'granting_access' && (
          <OnboardingModalShell
            key="granting"
            title="Granting your Artmosphere Studio access"
            showClose={false}
            closeOnBackdrop={false}
            zIndexClass="z-[200100]"
          >
            <div className="py-4 text-center" role="status" aria-live="polite" aria-busy="true">
              <div
                className="mx-auto h-12 w-12 rounded-full border-4 border-[#F5D99B] border-t-[#F59E0B] animate-spin"
                aria-hidden
              />
              <p className="mt-6 text-sm leading-relaxed text-[#5A4632]">
                Studio access is being registered after your signup — this usually takes just a moment.
              </p>
            </div>
          </OnboardingModalShell>
        )}
      </AnimatePresence>

      {simCheckoutOpen ? (
        <OnboardingModalShell wide title="Checkout add-on" zIndexClass="z-[200110]">
            <p className="mb-4 text-sm leading-relaxed text-[#5A4632]">
              Unlimited Studio + Storyboarding for{' '}
              {Math.round(premiumCheckoutMonths) === premiumCheckoutMonths
                ? `${premiumCheckoutMonths} month(s)`
                : `${premiumCheckoutMonths.toFixed(2)} month(s)`}{' '}
              based on your Artmo codes.
            </p>

            <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4">
              <div>
                <div className="rounded-2xl border border-stone-200/60 bg-white/70 p-4">
                  <div className="flex items-center justify-between gap-4 text-sm">
                    <span className="text-stone-700 font-medium">Total due today</span>
                    <span className="text-stone-900 font-bold">
                      {currencyLabel}
                      {premiumAddonTotal.toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-stone-600">Subtotal</span>
                      <span className="text-stone-900 font-semibold">
                        {currencyLabel}
                        {premiumAddonSubtotal.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-stone-600">GST (18%)</span>
                      <span className="text-stone-900 font-semibold">
                        {currencyLabel}
                        {premiumAddonGst.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-stone-600">
                    Invoice includes GST. Pay to unlock “Once Upon a Click” storyboarding.
                  </p>
                </div>

                <div className="mt-4 rounded-2xl border border-stone-200/60 bg-white/70 p-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div className="text-sm font-semibold text-stone-900">Billing address</div>
                    <div className="text-xs text-stone-500">For invoice</div>
                  </div>
                  <div className="space-y-3">
                    <input
                      value={billingName}
                      onChange={(e) => setBillingName(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="Full name"
                      aria-label="Billing full name"
                    />
                    <input
                      value={billingEmail}
                      onChange={(e) => setBillingEmail(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="Email"
                      aria-label="Billing email"
                    />
                    <input
                      value={billingAddress1}
                      onChange={(e) => setBillingAddress1(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="Address line 1"
                      aria-label="Billing address line 1"
                    />
                    <input
                      value={billingAddress2}
                      onChange={(e) => setBillingAddress2(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="Address line 2 (optional)"
                      aria-label="Billing address line 2"
                    />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        value={billingCity}
                        onChange={(e) => setBillingCity(e.target.value)}
                        className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                        placeholder="City"
                        aria-label="Billing city"
                      />
                      <input
                        value={billingState}
                        onChange={(e) => setBillingState(e.target.value)}
                        className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                        placeholder="State"
                        aria-label="Billing state"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input
                        value={billingPostalCode}
                        onChange={(e) => setBillingPostalCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                        placeholder="PIN code"
                        aria-label="Billing postal code"
                      />
                      <input
                        value={billingPhone}
                        onChange={(e) => setBillingPhone(e.target.value.replace(/\D/g, '').slice(0, 15))}
                        className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                        placeholder="Phone (optional)"
                        aria-label="Billing phone"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="rounded-2xl border border-stone-200/60 bg-white/70 p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold text-stone-900">Pay with</div>
                    <div className="text-xs text-stone-500">Card or UPI</div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      className={`flex-1 min-h-[40px] rounded-xl text-sm font-semibold border px-3 ${
                        paymentMethod === 'card'
                          ? 'border-amber-300 bg-amber-50 text-amber-900'
                          : 'border-stone-200 bg-white/70 text-stone-900 hover:bg-white'
                      }`}
                      onClick={() => {
                        setPaymentMethod('card');
                        setSimFormError('');
                      }}
                    >
                      Card
                    </button>
                    <button
                      type="button"
                      className={`flex-1 min-h-[40px] rounded-xl text-sm font-semibold border px-3 ${
                        paymentMethod === 'upi'
                          ? 'border-amber-300 bg-amber-50 text-amber-900'
                          : 'border-stone-200 bg-white/70 text-stone-900 hover:bg-white'
                      }`}
                      onClick={() => {
                        setPaymentMethod('upi');
                        setSimFormError('');
                      }}
                    >
                      UPI
                    </button>
                  </div>
                </div>

                {paymentMethod === 'upi' ? (
                  <div className="mt-4 space-y-3">
                    <input
                      value={simCardEmail}
                      onChange={(e) => setSimCardEmail(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="Email (required)"
                      aria-invalid={!simCardEmail ? 'true' : 'false'}
                    />
                    <input
                      value={simUpiId}
                      onChange={(e) => setSimUpiId(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="UPI ID (example: name@bank)"
                      aria-invalid={!isValidSimUpi() ? 'true' : 'false'}
                    />
                    <div className="text-xs text-stone-600">
                      We will simulate the UPI payment for this checkout.
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 space-y-3">
                    <input
                      value={simCardEmail}
                      onChange={(e) => setSimCardEmail(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="Email (required)"
                      aria-invalid={!simCardEmail ? 'true' : 'false'}
                    />
                    <input
                      value={simCardName}
                      onChange={(e) => setSimCardName(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="Name on card (required)"
                      aria-invalid={!simCardName ? 'true' : 'false'}
                    />
                    <input
                      value={simCardNumber}
                      onChange={(e) => setSimCardNumber(e.target.value)}
                      className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                      placeholder="Card number (required)"
                      aria-invalid={simCardNumber.replace(/\D/g, '').length < 12 ? 'true' : 'false'}
                    />

                    <div className="grid grid-cols-[minmax(0,1fr)_96px] gap-2">
                      <input
                        value={simCardExp}
                        onChange={(e) => setSimCardExp(formatExpiryInput(e.target.value))}
                        className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow"
                        inputMode="numeric"
                        autoComplete="cc-exp"
                        maxLength={5}
                        placeholder="MM/YY"
                        aria-invalid={!/^((0[1-9])|(1[0-2]))\/\d{2}$/.test(simCardExp || '') ? 'true' : 'false'}
                      />
                      <input
                        value={simCardCvc}
                        onChange={(e) => setSimCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        className="w-full min-w-0 border border-stone-200/90 rounded-xl px-3 py-2.5 text-sm text-stone-900 placeholder:text-stone-400 bg-white focus:border-amber-400 focus:outline-none focus:ring-2 focus:ring-amber-200/60 transition-shadow text-center"
                        inputMode="numeric"
                        autoComplete="cc-csc"
                        maxLength={4}
                        placeholder="CVC"
                        aria-invalid={(simCardCvc || '').trim().length < 3 ? 'true' : 'false'}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {simFormError ? (
              <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                {simFormError}
              </div>
            ) : null}
            {error ? (
              <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                {error}
              </div>
            ) : null}

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                className="art-btn-secondary flex-1 min-h-[48px] rounded-full text-sm font-semibold disabled:opacity-50"
                onClick={() => {
                  setSimCheckoutOpen(false);
                  setSimFormError('');
                }}
                disabled={loading}
              >
                Back
              </button>
              <button
                type="button"
                className="art-btn-primary flex-1 min-h-[48px] rounded-full text-sm font-semibold disabled:opacity-50"
                disabled={loading || quoteLoading || !canPaySimulated || !config.premiumSimulateAllowed}
                onClick={() => {
                  void completeSimulatedPremiumCheckout();
                }}
              >
                {loading && pendingAction === 'upgrade'
                  ? 'Processing…'
                  : !config.premiumSimulateAllowed
                    ? `Payments unavailable`
                    : `Pay ${currencyLabel}${premiumAddonTotal.toFixed(2)}`}
              </button>
            </div>
        </OnboardingModalShell>
      ) : null}

      {showSuccessModal ? (
        <OnboardingModalShell
          title={
            flowStep === 'success_premium'
              ? 'Thank you — your Unlimited add-on is active'
              : 'Your Artmosphere Studio access is active'
          }
          zIndexClass="z-[200100]"
        >
          <div className="text-center">
            <div className="text-4xl text-emerald-600" aria-hidden>
              ✓
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[#5A4632]">
              We sent a confirmation email to{' '}
              <strong className="break-all text-[#2F251B]">{user?.email || 'your account email'}</strong> with your
              activation details{flowStep === 'success_premium' ? ' and payment receipt' : ''}.
            </p>
            <button
              type="button"
              onClick={() => setWelcomeOpen(true)}
              className="art-btn-primary mt-6 w-full min-h-[48px] rounded-full text-sm font-semibold"
            >
              Continue
            </button>
            <p className="mt-4 text-xs text-[#8A7357]">
              Tap <strong className="text-[#2F251B]">Continue</strong> to create (or continue) your Artmosphere Studio
              artist profile.
            </p>
          </div>
        </OnboardingModalShell>
      ) : null}

      {welcomeOpen ? (
        <OnboardingModalShell
          title="Welcome to Artmosphere Studio"
          ariaLabelledBy="artmo-welcome-title"
          zIndexClass="z-[200105]"
        >
          <p className="text-center text-sm leading-relaxed text-[#5A4632]">
            Your creative space is ready. Tap below to open your Artist Corner.
          </p>
          <button
            type="button"
            onClick={goToArtistCorner}
            className="art-btn-primary mt-6 w-full min-h-[48px] rounded-full text-sm font-semibold"
          >
            Let&apos;s Go
          </button>
        </OnboardingModalShell>
      ) : null}
      </RoomSceneWrapper>
    </>
  );
}
